<template>
  <div class="view-wrapper">
    <span v-if="!isAuthenticated">
      Bitte anmelden, um die Applikation zu verwenden.
    </span>

    <template v-else>
      <BackButton />

      <h2 class="page-title">Alle Artikelbewegungen im Ausgabeautomat</h2>

      <div class="content">
        <section class="filters">
          <!-- Bewegungsart -->
          <Card class="filter-card">
            <template #content>
              <div class="filter-title">Bewegungsart</div>

              <div class="reason-grid">
                <div
                  v-for="opt in reasonOptions"
                  :key="opt.value"
                  class="reason-item"
                >
                  <Checkbox
                    v-model="selectedReasons"
                    :input-id="`reason-${opt.value}`"
                    :value="opt.value"
                  />
                  <label :for="`reason-${opt.value}`">{{ opt.label }}</label>
                </div>
              </div>
            </template>
          </Card>

          <!-- Zeitraum + Auftrag -->
          <Card class="filter-card">
            <template #content>
              <div class="filter-title">Zeitraum</div>

              <!-- Zeitraum -->
              <div class="date-range" style="margin-bottom: 1.5rem">
                <div class="date-range-row">
                  <div class="date-col">
                    <label class="date-label" for="startDate">Von</label>
                    <DatePicker
                      v-model="startDate"
                      input-id="startDate"
                      date-format="dd.mm.yy"
                      show-icon
                      :manual-input="true"
                      placeholder="Startdatum"
                    />
                  </div>

                  <div class="date-col">
                    <label class="date-label" for="endDate">Bis</label>
                    <DatePicker
                      v-model="endDate"
                      input-id="endDate"
                      date-format="dd.mm.yy"
                      show-icon
                      :manual-input="true"
                      placeholder="Enddatum"
                    />
                  </div>
                </div>

                <div v-if="dateError" class="date-warning">
                  {{ dateError }}
                </div>

                <div class="date-presets">
                  <Button
                    size="small"
                    label="Heute"
                    :outlined="activeDatePreset !== 0"
                    @click="setLastDays(0)"
                  />
                  <Button
                    size="small"
                    label="Letzte 7 Tage"
                    :outlined="activeDatePreset !== 7"
                    @click="setLastDays(7)"
                  />
                  <Button
                    size="small"
                    label="Letzte 30 Tage"
                    :outlined="activeDatePreset !== 30"
                    @click="setLastDays(30)"
                  />
                </div>
              </div>

              <div class="filter-title">Aufträge filtern (optional)</div>

              <!-- Auftrag -->
              <div class="order-filter">
                <MultiSelect
                  v-model="selectedOrders"
                  :options="orderOptions"
                  option-label="label"
                  option-value="value"
                  :show-clear="true"
                  placeholder="Aufträge auswählen..."
                  display="chip"
                  filter
                  :max-selected-labels="0"
                />
              </div>
            </template>
          </Card>

          <div class="filters-actions">
            <Button
              label="Filter anwenden"
              :loading="isLoading"
              :disabled="!canCallBackend"
              @click="callBackend"
            />
          </div>
        </section>

        <!-- TABLE -->
        <template v-if="hasAppliedFilters">
          <div class="table-toolbar">
            <IconField icon-position="right" class="global-search">
              <InputText
                v-model="globalFilterValue"
                placeholder="Suchbegriff eingeben..."
              />
              <InputIcon
                v-if="globalFilterValue"
                class="pi pi-times"
                @click="clearGlobalSearch"
                style="cursor: pointer"
              />
            </IconField>
          </div>

          <Card>
            <template #content>
              <div v-if="loadError" class="error">
                {{ loadError }}
              </div>

              <DataTable
                :value="movementsView"
                v-model:filters="filters"
                :global-filter-fields="globalFilterFields"
                data-key="movementId"
                table-layout="auto"
                paginator
                :rows="10"
                :rows-per-page-options="[5, 10, 20, 50, 100]"
                scrollable
                show-gridlines
                striped-rows
                removable-sort
                :loading="isLoading"
              >
                <template #empty>
                  <div style="text-align: center; padding: 0.5rem">
                    Keine Daten für die gewählten Filter gefunden.
                  </div>
                </template>

                <Column
                  field="idProduct"
                  header="Artikel"
                  sortable
                  filter
                  :show-filter-menu="false"
                  filter-placeholder="Filtern..."
                />

                <Column
                  field="productName"
                  header="Bezeichnung"
                  sortable
                  filter
                  :show-filter-menu="false"
                  filter-placeholder="Filtern..."
                />

                <Column
                  field="idCostCenter"
                  header="Auftrag"
                  sortable
                  filter
                  :show-filter-menu="false"
                  filter-placeholder="Filtern..."
                />

                <Column
                  field="quantity"
                  header="Anzahl"
                  sortable
                  filter
                  :show-filter-menu="false"
                  filter-placeholder="Filtern..."
                />

                <Column
                  field="reasonText"
                  header="Bewegungsart"
                  sortable
                  filter
                  :show-filter-menu="false"
                  filter-placeholder="Filtern..."
                />

                <Column
                  field="eventTime"
                  header="Zeitpunkt"
                  sortable
                  filter-field="eventtimeFormatted"
                  filter
                  :show-filter-menu="false"
                  filter-placeholder="Filtern..."
                >
                  <template #body="{ data }">
                    {{ formatIsoDateTime(data.eventTime) }}
                  </template>
                </Column>

                <Column
                  field="employeeName"
                  header="Benutzer:in"
                  sortable
                  filter
                  :show-filter-menu="false"
                  filter-placeholder="Filtern..."
                />
              </DataTable>
            </template>
          </Card>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import { useIsAuthenticated } from "@/composition-api/useIsAuthenticated";
  import { useBackendApi } from "@/composition-api/useBackendApi";
  import type { StockMovement, CostCenter } from "@apis/backend";
  import { reasonMap } from "@/types/reasonMap";
  import { formatIsoDateTime } from "@/utils/dateFormats";
  import Card from "primevue/card";
  import DataTable from "primevue/datatable";
  import Column from "primevue/column";
  import InputText from "primevue/inputtext";
  import IconField from "primevue/iconfield";
  import InputIcon from "primevue/inputicon";
  import DatePicker from "primevue/datepicker";
  import Button from "primevue/button";
  import BackButton from "@/components/BackButton.vue";
  import Checkbox from "primevue/checkbox";
  import MultiSelect from "primevue/multiselect";
  import { useToast } from "primevue/usetoast";

  const toast = useToast();
  const isAuthenticated = useIsAuthenticated();
  const apis = useBackendApi();

  const isLoading = ref(false);
  const loadError = ref<string | null>(null);
  const hasAppliedFilters = ref(false);

  const movements = ref<StockMovement[]>([]);

  onMounted(() => {
    apis.callWhenTokenInitialized(() => {
      if (!isAuthenticated.value) return;
      void getCostCenters();
    });
  });

  async function getCostCenters() {
    try {
      const data: CostCenter[] = await apis.reporting.fetchCostCenters();

      orderOptions.value =
        (data ?? []).map((cc) => ({
          value: cc.costCenterId,
          label: `WA ${cc.costCenterId} | ${cc.costCenterName}`,
        })) ?? [];
    } catch (error) {
      console.error("Fehler beim Laden der Werkstattaufträge", error);
      toast.add({
        severity: "error",
        summary: "Fehler",
        detail: "Werkstattaufträge konnten nicht geladen werden.",
        life: 5000,
      });
      orderOptions.value = [];
    }
  }

  // ==============================
  // Reason filter
  // ==============================

  const reasonOptions = Object.entries(reasonMap)
    .filter(([, v]) => v.selectable)
    .map(([k, v]) => ({
      value: Number(k),
      label: v.label,
    }));

  const selectedReasons = ref<number[]>([2, 10, 20, 21]);

  // ==============================
  // Date filter
  // ==============================

  const startDate = ref<Date | null>(new Date());
  const endDate = ref<Date | null>(new Date());

  function isSameDay(a: Date | null, b: Date | null) {
    if (!a || !b) return false;

    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  function getPresetStart(days: number) {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
  }

  const activeDatePreset = computed<number | null>(() => {
    if (!startDate.value || !endDate.value) return null;

    const today = new Date();

    if (
      isSameDay(startDate.value, getPresetStart(0)) &&
      isSameDay(endDate.value, today)
    ) {
      return 0;
    }

    if (
      isSameDay(startDate.value, getPresetStart(7)) &&
      isSameDay(endDate.value, today)
    ) {
      return 7;
    }

    if (
      isSameDay(startDate.value, getPresetStart(30)) &&
      isSameDay(endDate.value, today)
    ) {
      return 30;
    }

    return null;
  });

  const normalizedRange = computed(() => {
    if (!startDate.value || !endDate.value) return null;

    const start = new Date(startDate.value);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate.value);
    end.setHours(23, 59, 59, 999);

    return {
      startIso: start.toISOString(),
      endIso: end.toISOString(),
    };
  });

  const dateError = computed<string | null>(() => {
    if (!startDate.value) return "Startdatum fehlt.";
    if (!endDate.value) return "Enddatum fehlt.";
    if (!normalizedRange.value) return "Ungültiger Zeitraum.";
    if (normalizedRange.value.endIso < normalizedRange.value.startIso)
      return "Enddatum darf nicht vor dem Startdatum liegen.";
    return null;
  });

  function setLastDays(days: number) {
    startDate.value = getPresetStart(days);
    endDate.value = new Date();
  }

  // ==============================
  // Auftrag filter
  // ==============================

  type OrderOption = { value: string; label: string };

  const orderOptions = ref<OrderOption[]>([]);
  const selectedOrders = ref<string[]>([]);

  // ==============================
  // Backend Call
  // ==============================

  const canCallBackend = computed(() => {
    return selectedReasons.value.length > 0 && dateError.value === null;
  });

  async function callBackend() {
    const range = normalizedRange.value;
    if (!range) return;
    if (dateError.value) return;
    if (selectedReasons.value.length === 0) return;

    hasAppliedFilters.value = true;

    isLoading.value = true;
    loadError.value = null;

    try {
      const response = await apis.reporting.fetchStockMovements({
        eventtimeGte: range.startIso,
        eventtimeLte: range.endIso,
        reason: selectedReasons.value,
        costCenters:
          selectedOrders.value.length > 0 ? selectedOrders.value : undefined,
      });

      movements.value = response ?? [];
    } catch (error) {
      console.error("Fehler beim Laden der Bewegungen", error);
      toast.add({
        severity: "error",
        summary: "Fehler",
        detail: "Bewegungen konnten nicht geladen werden.",
        life: 5000,
      });
      movements.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  // ==============================
  // View mapping
  // ==============================

  const movementsView = computed(() =>
    (movements.value ?? []).map((m) => {
      const reason = reasonMap[m.reason ?? -1];

      return {
        ...m,
        idProduct: m.idProduct?.replace(/^BDS/, "BBW ")?.replace(/^OY/, "OY "),
        eventtimeFormatted: formatIsoDateTime(m.eventTime),
        reasonText: reason.label,
      };
    }),
  );

  // ==============================
  // Global Search (DataTable intern)
  // ==============================

  const globalFilterValue = ref("");

  const clearGlobalSearch = () => {
    globalFilterValue.value = "";
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filters = ref<Record<string, any>>({
    global: { value: null, matchMode: "contains" },
    idProduct: { value: null, matchMode: "contains" },
    productName: { value: null, matchMode: "contains" },
    idCostCenter: { value: null, matchMode: "contains" },
    quantity: { value: null, matchMode: "contains" },
    reasonText: { value: null, matchMode: "contains" },
    eventtimeFormatted: { value: null, matchMode: "contains" },
    employeeName: { value: null, matchMode: "contains" },
  });

  const globalFilterFields = [
    "idProduct",
    "productName",
    "idCostCenter",
    "quantity",
    "reasonText",
    "eventtimeFormatted",
    "employeeName",
  ];

  watch(globalFilterValue, (v) => {
    filters.value.global.value = v?.trim() || null;
  });
</script>

<style scoped lang="scss">
  .view-wrapper {
    width: 99%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
  }

  .page-title {
    margin: 1rem 0 0;
    text-align: left;
  }

  .content {
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(18rem, 1fr));
    gap: 1rem;
    align-items: stretch;
  }

  .filter-card {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .filters-actions {
    grid-column: 1 / -1;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .filter-title {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0 0 0.75rem 0;
  }

  .date-range {
    width: 100%;
  }

  .date-range-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    align-items: end;
  }

  .date-col {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .date-label {
    font-size: 0.95rem;
    font-weight: 600;
  }

  .date-warning {
    margin-top: 0.5rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid #f3c2c2;
    background: #fff3f3;
    border-radius: 6px;
    font-size: 0.95rem;
    box-sizing: border-box;
  }

  .date-presets {
    margin-top: 0.75rem;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
  }

  .date-range :deep(.p-datepicker),
  .date-range :deep(.p-inputtext) {
    width: 100%;
  }

  /* ===== Table toolbar ===== */
  .table-toolbar {
    display: flex;
    justify-content: center;
  }

  .global-search :deep(input) {
    min-width: 22rem;
    font-size: 1.2rem;
    padding: 0.4rem 0.6rem;
    border: 1px solid #c5c5c5;
    border-radius: 4px;
    background: #fff;
    box-sizing: border-box;
  }

  /* ===== Filter internals ===== */
  .reason-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .reason-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.35rem 1rem;
    margin-top: 0.25rem;
  }

  .reason-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .reason-item label {
    font-size: 1.1rem;
  }

  .order-filter {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }

  .error {
    width: 100%;
    margin: 0 0 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid #f3c2c2;
    background: #fff3f3;
    border-radius: 6px;
    box-sizing: border-box;
  }
</style>
